import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiSDK } from '../../contexts/apiSdkContext';
import { useCompanyId } from '../../contexts/CompaniesAreasContext';
import { AppContext } from '../../pages/layout';

type TCompanyDeleteHook = {
  isError: boolean;
  isLoading: boolean;
  mutate: UseMutationResult<void, unknown, string, unknown>['mutate'];
};

function useDeleteCompany(): TCompanyDeleteHook {
  const sdk = useApiSDK();
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { companyId, resetCompanyId } = useCompanyId();

  const { isError, isLoading, mutate } = useMutation({
    mutationFn: async (id: string) => {
      await sdk.companies.delete(id);
    },
    onSuccess: async (_contexts, variables) => {
      await queryClient.invalidateQueries(['companies']);
      await queryClient.invalidateQueries(['company-areas']);

      appContext?.crudAlert('delete', 'company', 'f');

      if (variables === companyId?.id) {
        resetCompanyId();
      }

      navigate('/settings/companies');
    },
    onError: async (error: any) => {
      if (error) {
        const responseErrors = error.response.data.errors;
        responseErrors.forEach((err: { message: string; description: string }) => {
          appContext?.systemAlert('error', err.description);
        });
      }
    },
  });

  return { isError, isLoading, mutate };
}

export { useDeleteCompany };
